// Card 2358: first-touch marketing attribution.
//
// GA is unreliable (consent defaults to denied), so we keep our own first-touch record of how the customer
// arrived (utm_* params, a known ad click id, landing path and external referrer host) and send it to the
// checkout API once a cart exists. The server stores it on the cart and copies it to the order for reporting.
//
// Only kept with MARKETING consent (the 'marketing' entry of spaseekers:cookie-preferences, set by
// CookieNotice). Before the customer has decided, the landing is held in memory only; it is written to
// localStorage only if they grant marketing consent, and discarded if they deny it. Withdrawing marketing
// consent deletes the stored attribution and stops it being sent. Analytics consent alone has no effect.
//
// No PII: the landing page is the path only (no query string or fragment) and the referrer is the host only.

const STORAGE_KEY = 'spaseekers:attribution';
const CONSENT_KEY = 'spaseekers:cookie-preferences';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // first touch counts for 30 days
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];
// Checked in this order; the first present is kept.
const CLICK_ID_PARAMS = ['gclid', 'fbclid', 'msclkid', 'cjevent'];

let pendingAttribution = null;

const isBrowser = () => typeof window !== 'undefined';

const clean = (value, maxLength = 200) => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed.substring(0, maxLength) : null;
};

// The consent category attribution depends on (CookieNotice cookie name; values 'granted' / 'denied').
const CONSENT_CATEGORY = 'marketing';

export const hasMarketingConsent = (preferences = null) => {
  try {
    const prefs =
      preferences ||
      (isBrowser() && JSON.parse(localStorage.getItem(CONSENT_KEY)));
    return !!prefs && prefs[CONSENT_CATEGORY] === 'granted';
  } catch (e) {
    return false;
  }
};

const removeStoredAttribution = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

// Builds the attribution for a landing, or null when it carries no marketing signal (a direct or internal
// visit does not replace or create a first touch).
export const parseAttribution = (
  { search = '', pathname = '/', referrer = '', host = '' },
  now = new Date(),
) => {
  const params = new URLSearchParams(search);
  const attribution = {};

  UTM_PARAMS.forEach(name => {
    const value = clean(params.get(name));
    if (value) {
      // utm_source -> utmSource
      attribution[name.replace(/_(\w)/, (_, c) => c.toUpperCase())] = value;
    }
  });

  const clickIdType = CLICK_ID_PARAMS.find(name => clean(params.get(name)));
  if (clickIdType) {
    attribution.clickIdType = clickIdType;
    attribution.clickId = clean(params.get(clickIdType), 500);
  }

  let referrerHost = null;
  try {
    referrerHost = referrer ? new URL(referrer).hostname.toLowerCase() : null;
  } catch (e) {
    referrerHost = null;
  }
  const ownHost = (host || '').toLowerCase().replace(/^www\./, '');
  if (
    referrerHost &&
    ownHost &&
    (referrerHost === ownHost || referrerHost.endsWith(`.${ownHost}`))
  ) {
    referrerHost = null; // internal navigation, not a source
  }
  if (referrerHost) {
    attribution.referrerHost = referrerHost.substring(0, 255);
  }

  if (Object.keys(attribution).length === 0) {
    return null;
  }

  attribution.landingPath = clean(pathname, 500) || '/';
  attribution.firstTouchUtc = now.toISOString();
  return attribution;
};

export const getStoredAttribution = (now = Date.now()) => {
  if (!isBrowser()) {
    return null;
  }
  if (!hasMarketingConsent()) {
    // Consent missing or withdrawn: never use (and do not keep) anything stored.
    removeStoredAttribution();
    return null;
  }
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!stored || !stored.firstTouchUtc) {
      return null;
    }
    if (now - new Date(stored.firstTouchUtc).getTime() > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch (e) {
    return null;
  }
};

const storeAttribution = attribution => {
  // First touch wins: never overwrite a live stored attribution.
  if (!attribution || !hasMarketingConsent() || getStoredAttribution()) {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch (e) {
    // Storage full or blocked; attribution is best effort.
  }
};

// Call once on the landing page view (gatsby-browser onClientEntry).
export const captureLandingAttribution = () => {
  if (!isBrowser()) {
    return;
  }
  try {
    const attribution = parseAttribution({
      search: window.location.search,
      pathname: window.location.pathname,
      referrer: document.referrer,
      host: window.location.hostname,
    });

    if (!localStorage.getItem(CONSENT_KEY)) {
      // Not decided yet: hold in memory only until the customer chooses.
      if (attribution) {
        pendingAttribution = attribution;
      }
    } else if (hasMarketingConsent()) {
      storeAttribution(attribution);
    } else {
      // Marketing denied: drop this landing and anything stored earlier.
      removeStoredAttribution();
    }
  } catch (e) {
    // Attribution must never break the page.
  }
};

// Call whenever the customer saves cookie preferences (CookieNotice).
export const onAttributionConsentUpdated = preferences => {
  if (!isBrowser()) {
    return;
  }
  try {
    if (hasMarketingConsent(preferences)) {
      storeAttribution(pendingAttribution);
    } else {
      // Denied or withdrawn: drop the pending landing and delete any stored attribution.
      removeStoredAttribution();
    }
  } catch (e) {
    // ignore
  }
  pendingAttribution = null;
};

// Sends the stored attribution to the checkout API for the current cart. Call after a successful add to
// cart (the cart cookie exists by then). The server keeps the first value per cart, so repeat calls are safe.
export const sendCartAttribution = async checkoutRequest => {
  if (!isBrowser() || !hasMarketingConsent()) {
    return;
  }
  const attribution = getStoredAttribution();
  if (!attribution) {
    return;
  }
  try {
    await checkoutRequest(
      'POST',
      'v1/cart/attribution',
      attribution,
      '',
      '',
      1,
    );
  } catch (e) {
    // Best effort: attribution must never affect the checkout.
  }
};
